const legacyMetricLabels = {
  flow: "Caudal",
  pressure: "Presión",
  temperature: "Temperatura",
};

const providerDefaultMetricNames = new Set(["flow", "pressure", "temperature"]);

export const localizedMetricDisplayName = (metricCode, displayName) => {
  const fallback = legacyMetricLabels[metricCode] || metricCode;
  const suppliedName = String(displayName || "").trim();

  // The backend remains the source of metric metadata. Translate only its
  // generic English defaults; provider-specific and already-localized names
  // stay untouched.
  if (
    !suppliedName ||
    suppliedName.toLowerCase() === metricCode ||
    providerDefaultMetricNames.has(suppliedName.toLowerCase())
  ) {
    return fallback;
  }

  return suppliedName;
};

const toTimestamp = (value) => {
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const selectedSensorEntries = (data = [], selectedRowIds = []) => {
  const selected = new Set(selectedRowIds);
  return (Array.isArray(data) ? data : []).filter((sensor) =>
    selected.has(sensor?.id)
  );
};

export const analyticsMetricOptions = (sensors = []) => {
  const metrics = new Map();

  sensors.forEach((sensor) => {
    (Array.isArray(sensor?.data) ? sensor.data : []).forEach((metric) => {
      if (!metric?.category || metrics.has(metric.category)) return;
      metrics.set(metric.category, {
        metricCode: metric.category,
        displayName: localizedMetricDisplayName(
          metric.category,
          metric.displayName
        ),
        unit: metric.unit || "",
      });
    });
  });

  return Array.from(metrics.values());
};

export const resolveAnalyticsSelection = (
  data = [],
  selectedRowIds = [],
  currentMetricCode = ""
) => {
  const selectedSensors = selectedSensorEntries(data, selectedRowIds);
  const metricOptions = analyticsMetricOptions(selectedSensors);
  const selectedMetric =
    metricOptions.find((metric) => metric.metricCode === currentMetricCode) ||
    metricOptions[0] ||
    null;
  const firstSensor = selectedSensors[0];

  return {
    sensorExternalIds: selectedSensors.map((sensor) => sensor.deviceId),
    metricOptions,
    selectedMetric,
    city: firstSensor?.location?.city || "",
    location: firstSensor?.location?.specificLocation || "",
  };
};

export const mapAnalyticsChartSeries = ({
  data = [],
  sensorExternalIds = [],
  metricCode,
  startDate = null,
  endDate = null,
  colors = [],
} = {}) => {
  if (!metricCode || sensorExternalIds.length === 0) return [];

  const selectedSensors = new Set(sensorExternalIds);
  const startTimestamp = startDate == null ? null : toTimestamp(startDate);
  const endTimestamp = endDate == null ? null : toTimestamp(endDate);

  return (Array.isArray(data) ? data : []).flatMap((sensor, sensorIndex) => {
    if (!selectedSensors.has(sensor?.deviceId)) return [];

    const metric = (Array.isArray(sensor?.data) ? sensor.data : []).find(
      (candidate) => candidate?.category === metricCode
    );
    if (!metric) return [];

    const pointsByTimestamp = new Map();
    (Array.isArray(metric.measurements) ? metric.measurements : []).forEach(
      (measurement) => {
        const timestamp = toTimestamp(measurement?.date);
        const value = Number(measurement?.value);
        if (timestamp == null || !Number.isFinite(value)) return;
        if (startTimestamp != null && timestamp < startTimestamp) return;
        if (endTimestamp != null && timestamp > endTimestamp) return;

        pointsByTimestamp.set(timestamp, {
          x: new Date(timestamp),
          y: value,
          category: metricCode,
        });
      }
    );

    const points = Array.from(pointsByTimestamp.values()).sort(
      (left, right) => left.x - right.x
    );
    if (points.length === 0) return [];

    return [
      {
        id: sensor.title || sensor.deviceId,
        title: sensor.title || sensor.deviceId,
        color: colors.length ? colors[sensorIndex % colors.length] : undefined,
        data: points,
        category: metricCode,
      },
    ];
  });
};

export const metricAxisLabel = (metric) => {
  if (!metric) return "";
  return metric.unit
    ? `${metric.displayName} (${metric.unit})`
    : metric.displayName;
};

export const metricHistoryTitle = (metric) =>
  metric?.displayName
    ? `Historial de ${metric.displayName}`
    : "Historial de mediciones";
